// ST-1729 -Claude- Public About page for Nearrme.
import { ChangeEvent, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '@nearrme/ui-web';
import { BAG_MARK_SVG, BRAND } from '@nearrme/brand';
import { submitContactMessage } from '../api/contactMessages';

const VALUES = [
  {
    title: 'Verified stores',
    body: 'Nearrme helps customers find real nearby stores and dealers that can stand behind the products they sell.',
    mark: 'VS',
    tone: 'from-emerald-600 to-slate-900',
  },
  {
    title: 'Original warranty',
    body: 'The platform is designed around manufacturer-backed products, purchase confidence, and after-sales support.',
    mark: 'OW',
    tone: 'from-sky-600 to-emerald-700',
  },
  {
    title: 'Local commerce',
    body: 'We keep discovery close to the customer, so trusted local businesses can compete on availability and service.',
    mark: 'LC',
    tone: 'from-amber-500 to-emerald-800',
  },
];

const PRINCIPLES = [
  {
    title: 'Manufacturer verified stores',
    body: 'Find sellers that brands and manufacturers can stand behind, so the store relationship is clear before you buy.',
    mark: 'MV',
    tone: 'from-emerald-600 to-slate-900',
  },
  {
    title: 'Original manufacturer warranty',
    body: 'Buy products with confidence that warranty, invoice, and after-sales support are part of the purchase journey.',
    mark: 'OW',
    tone: 'from-sky-600 to-emerald-700',
  },
  {
    title: 'Nearby inventory before travel',
    body: 'Check local availability before leaving home, then choose the store that is practical for pickup or purchase.',
    mark: 'NI',
    tone: 'from-amber-500 to-emerald-800',
  },
  {
    title: 'Trusted local commerce',
    body: 'Support nearby retailers while still getting the authenticity and service standards customers expect online.',
    mark: 'LC',
    tone: 'from-rose-600 to-slate-900',
  },
];

export function AboutPage() {
  const [contact, setContact] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'saving' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');

  const updateContact = (field: keyof typeof contact) => (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setContact((current) => ({ ...current, [field]: event.target.value }));
  };

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatus('saving');
    setError('');

    try {
      await submitContactMessage({ ...contact, sourcePage: '/about' });
      setContact({ firstName: '', lastName: '', email: '', phone: '', message: '' });
      setStatus('sent');
    } catch (err) {
      setStatus('error');
      setError(err instanceof Error ? err.message : 'Could not send your message.');
    }
  };

  return (
    <div className="animate-fade-in">
      <section className="bg-white dark:bg-slate-950 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-14 sm:py-20 grid lg:grid-cols-[1fr_360px] gap-10 items-center">
          <div>
            <div
              className="h-20 w-20 mb-6"
              dangerouslySetInnerHTML={{ __html: BAG_MARK_SVG }}
            />
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
              About Nearrme
            </p>
            <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight text-slate-950 dark:text-slate-50">
              Find trusted products from verified nearby stores.
            </h1>
            <p className="mt-5 text-lg text-slate-600 dark:text-slate-300 max-w-3xl">
              Nearrme connects consumers with authentic products, manufacturer warranty confidence, and physical stores that can serve them locally.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link
                to="/"
                className="inline-flex justify-center items-center h-11 px-5 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700"
              >
                Search Nearrme
              </Link>
              <Link
                to="/business/signup"
                className="inline-flex justify-center items-center h-11 px-5 rounded-lg border border-slate-300 dark:border-slate-700 text-slate-700 dark:text-slate-200 text-sm font-semibold hover:bg-slate-100 dark:hover:bg-slate-800"
              >
                List a business
              </Link>
            </div>
          </div>

          <div className="rounded-xl bg-gradient-to-br from-brand-700 via-emerald-700 to-slate-950 text-white p-6">
            <div className="text-sm font-semibold uppercase tracking-wide text-emerald-100">
              Genuine product confidence
            </div>
            <div className="mt-5 h-24 rounded-xl border border-white/15 bg-white/10 flex items-center justify-center">
              <div className="h-16 w-16 rounded-2xl bg-white text-brand-700 flex items-center justify-center text-2xl font-black shadow-lg">
                NM
              </div>
            </div>
            <div className="mt-5 text-2xl font-bold tracking-tight">
              Buy genuine. Get served locally.
            </div>
            <p className="mt-4 text-sm leading-6 text-emerald-50">
              Nearrme is built to help customers choose verified stores, original manufacturer warranty, and real support after purchase.
            </p>
            <p className="mt-6 text-xs text-emerald-100">
              {BRAND.trademarkNotice}
            </p>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid md:grid-cols-3 gap-4">
          {VALUES.map((item) => (
            <Card key={item.title} padded className="overflow-hidden">
              <div className={`h-28 rounded-lg bg-gradient-to-br ${item.tone} flex items-center justify-center text-white mb-5`}>
                <div className="h-16 w-16 rounded-2xl border border-white/20 bg-white/15 flex items-center justify-center text-xl font-black tracking-wide">
                  {item.mark}
                </div>
              </div>
              <h2 className="text-lg font-bold text-slate-950 dark:text-slate-50">{item.title}</h2>
              <p className="mt-3 text-sm leading-6 text-slate-600 dark:text-slate-300">{item.body}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="bg-slate-50 dark:bg-slate-900/60 border-y border-slate-200 dark:border-slate-800">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl font-bold text-slate-950 dark:text-slate-50">What Nearrme stands for</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {PRINCIPLES.map((item) => (
              <div
                key={item.title}
                className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 overflow-hidden"
              >
                <div className={`h-28 bg-gradient-to-br ${item.tone} flex items-center justify-center text-white`}>
                  <div className="relative h-16 w-16 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center text-lg font-black tracking-wide">
                    {item.mark}
                    <span className="absolute -right-2 -bottom-2 h-7 w-7 rounded-full bg-white text-brand-700 flex items-center justify-center text-xs font-black">
                      ✓
                    </span>
                  </div>
                </div>
                <div className="p-4">
                  <h3 className="text-sm font-bold text-slate-950 dark:text-slate-50">{item.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">{item.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-4 py-12">
        <div className="grid lg:grid-cols-[0.9fr_1.1fr] gap-8 items-start">
          <div>
            <p className="text-sm font-semibold uppercase tracking-wide text-brand-700 dark:text-brand-300">
              Contact us
            </p>
            <h2 className="mt-3 text-3xl font-bold text-slate-950 dark:text-slate-50">
              Questions about genuine products, stores, or Nearrme partnerships?
            </h2>
            <p className="mt-4 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Send us the details and the Nearrme team will route the message to the right person. Businesses can use this form for listing, manufacturer verification, warranty, or support questions.
            </p>
            <div className="mt-6 rounded-lg border border-emerald-200 dark:border-emerald-900 bg-emerald-50 dark:bg-emerald-950/40 p-4">
              <div className="text-sm font-bold text-emerald-900 dark:text-emerald-100">
                We help you reach the right trusted source
              </div>
              <p className="mt-2 text-sm leading-6 text-emerald-800 dark:text-emerald-200">
                Whether you need help finding an authorized dealer, checking genuine product availability, understanding manufacturer warranty, or listing a verified business, Nearrme routes your request to the team best placed to respond.
              </p>
            </div>
          </div>

          <Card padded>
            <form onSubmit={handleContactSubmit} className="space-y-4" aria-label="Contact Nearrme">
              <div className="grid sm:grid-cols-2 gap-4">
                <ContactField label="First name" value={contact.firstName} onChange={updateContact('firstName')} required autoComplete="given-name" />
                <ContactField label="Last name" value={contact.lastName} onChange={updateContact('lastName')} required autoComplete="family-name" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <ContactField label="Email" type="email" value={contact.email} onChange={updateContact('email')} required autoComplete="email" />
                <ContactField label="Phone" type="tel" value={contact.phone} onChange={updateContact('phone')} required autoComplete="tel" />
              </div>
              <label className="block">
                <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Message</span>
                <textarea
                  value={contact.message}
                  onChange={updateContact('message')}
                  required
                  minLength={10}
                  maxLength={2000}
                  rows={5}
                  className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 py-2 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  placeholder="Tell us how we can help."
                />
              </label>

              <div aria-live="polite" className="min-h-6">
                {status === 'sent' && (
                  <p className="text-sm text-emerald-700 dark:text-emerald-300">
                    Message received. We will follow up with you.
                  </p>
                )}
                {status === 'error' && (
                  <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
                )}
              </div>

              <button
                type="submit"
                disabled={status === 'saving'}
                className="inline-flex w-full sm:w-auto justify-center items-center h-11 px-5 rounded-lg bg-brand-600 text-white text-sm font-semibold hover:bg-brand-700 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {status === 'saving' ? 'Sending...' : 'Send message'}
              </button>
            </form>
          </Card>
        </div>
      </section>
    </div>
  );
}

interface ContactFieldProps {
  label: string;
  value: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
}

function ContactField({ label, value, onChange, type = 'text', required, autoComplete }: ContactFieldProps) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">{label}</span>
      <input
        type={type}
        value={value}
        onChange={onChange}
        required={required}
        autoComplete={autoComplete}
        className="w-full h-11 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 px-3 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500"
      />
    </label>
  );
}
